<script setup lang="ts">
import { acceptInvite } from '@/api/calls/backend-calls/invite-calls'
import type { State } from '@/store/store_types'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { ActionTypes } from '../../store/store_types'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'
import ErrorCard from './ErrorCard.vue'
import ProcessingCard from './ProcessingCard.vue'
import SuccessCard from './SuccessCard.vue'

const store = useStore<State>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const code = ref('')
const status = ref<'processing' | 'success' | 'error' | null>(null)
const dirty = computed(() => code.value.trim() !== '')

const submit = async () => {
  console.log('Submitted invite code:', code.value)
  status.value = 'processing'

  acceptInvite(code.value)
    .then(() => {
      status.value = 'success'
      // handle user data refresh
    })
    .catch((error) => {
      console.error('Error joining fleet:', error)
      status.value = 'error'
    })
}

const refreshData = () => {
  store.dispatch(ActionTypes.refetchData)
}
</script>
<template>
  <ProcessingCard
    v-if="status === 'processing'"
    title="Joining Fleet"
    message="Please wait while we process your request..."
  />

  <ErrorCard
    v-else-if="status === 'error'"
    title="Failed to Join Fleet"
    message="There was an issue joining the fleet. Please check your invite code and try again."
    actionName="Try Again"
    :action="() => submit()"
    @close="status = null"
  />

  <SuccessCard
    v-else-if="status === 'success'"
    title="Successfully Joined Fleet"
    message="You have successfully joined the fleet! You can now access fleet features."
    @close="refreshData"
  />

  <v-card class="rounded-lg pa-10" v-else>
    <v-card-title class="d-flex align-center">
      <v-btn icon @click="emit('close')" flat density="comfortable" color="transparent">
        <v-icon color="primary">mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />
      Join an existing fleet
      <v-spacer />
    </v-card-title>
    <v-card-text class="text-center">
      <v-text-field
        v-model="code"
        label="Invite code"
        variant="outlined"
        class="mt-4"
      ></v-text-field>
    </v-card-text>
    <v-card-actions class="justify-center">
      <OutlinedBtn @click="emit('close')" shape="box" class="mr-1"> Cancel </OutlinedBtn>
      <ElevatedBtn @click="submit" shape="box" class="ml-1" :disabled="!dirty">
        Continue
      </ElevatedBtn>
    </v-card-actions>
  </v-card>
</template>
